import { Flex, Image, Spin, Typography } from 'antd';
import type { FC } from 'react';
import { useMemo } from 'react';
import type { DriveFolderType, SerializedMeterModel } from '@enmon/commons/lib/tools';
import { useMeterModelFiles } from 'api/hooks/meter-models/useMeterModelFiles';
import { Icon } from 'components/icon';
import { useTheme } from 'styled-components';
import type { PopulatedCombinedMeter } from '@/lib/services';
import { useFiles } from '@/api/hooks/files/useFiles';
import { Collection, FileType, MeterFileValue } from '@enmon/commons/lib/enmon';
import { useDocPreviewModal } from './DocPreviewModal';
import { FileCard } from './FileCard';
import { getImageSrcFromBuffer } from './helpers';

const { Text } = Typography;

export interface ModelFilesGalleryProps {
  model?: SerializedMeterModel;
  folderType: DriveFolderType;
  emptyMessage: string;
  source: 'model' | 'meter';
  meter?: PopulatedCombinedMeter | null;
  pageSize?: number;
  enablePreview?: boolean;
  visible?: boolean;
}

export const ModelFilesGallery: FC<ModelFilesGalleryProps> = ({
  model,
  folderType,
  emptyMessage,
  source,
  meter = null,
  pageSize = 5,
  enablePreview = false,
  visible = true,
}) => {
  const theme = useTheme();
  const {
    Modal: DocPreviewModal,
    isOpen: isOpenDocPreviewModal,
    url,
    fileName,
    isLoading: isLoadingDocPreviewModal,
    handleOpenDocFile,
    closeModal: closeDocPreviewModal,
  } = useDocPreviewModal(model?.id);

  const queryParams = useMemo(
    () => ({
      modelId: model?.id,
      folderType,
      pageSize,
      resize: true as const,
    }),
    [model?.id, folderType, pageSize],
  );

  const shouldFetchModelFiles = source === 'model' && visible && !!model?.id && !!model?.folder_drive_id;
  const shouldFetchMeterFiles = source === 'meter' && visible && !!meter?._id;

  const {
    data: files,
    isLoading: isLoadingModelFiles,
    isError: isModelError,
  } = useMeterModelFiles(queryParams, { enabled: shouldFetchModelFiles });

  const {
    data: meterFiles,
    isLoading: isLoadingMeterFiles,
    isError: isMeterError,
  } = useFiles({ collections: [Collection.Meters], meters: [meter?._id] }, { enabled: shouldFetchMeterFiles });

  const isLoading = isLoadingModelFiles || isLoadingMeterFiles;

  const modelFileItems = useMemo(() => {
    if (!files) return [];

    return files.map(({ metadata: { id, name, mimeType }, buffer }) => {
      const isImage = mimeType?.startsWith('image/');
      const dataUrl = buffer ? getImageSrcFromBuffer(buffer) : undefined;
      return {
        id,
        name,
        mimeType,
        imageSrc: isImage ? dataUrl : undefined,
        driveFileId: !isImage ? id : undefined,
        isImage,
      };
    });
  }, [files]);

  const meterFileItems = useMemo(() => {
    if (!meterFiles?.result) return [];

    const photoFileValues = [MeterFileValue.Context, MeterFileValue.Detail];

    return meterFiles.result
      .filter(file => file.type === FileType.Meter && photoFileValues.includes(file.value))
      .map(({ _id, name, signedUrl, ext, value }) => ({
        id: _id,
        name,
        mimeType: ext,
        imageSrc: signedUrl,
        driveFileId: undefined,
        isImage: true,
        value,
      }));
  }, [meterFiles]);

  if (!visible) return null;

  if (isLoading) {
    return (
      <Flex align="center" justify="center" orientation="vertical" gap={16}>
        <Spin size="large" />
        <Text type="secondary">Loading {folderType}...</Text>
      </Flex>
    );
  }

  const allItems = (() => {
    if (source === 'model') return modelFileItems;
    if (source === 'meter') return meterFileItems;
    return [...modelFileItems, ...meterFileItems];
  })();

  const hasError = source === 'model' ? isModelError : isMeterError;
  const isEmptyFolder = hasError || allItems.length === 0;

  return (
    <>
      <Flex orientation="vertical" gap={16}>
        {isEmptyFolder ? (
          <Flex align="center" justify="center" orientation="vertical" gap={16}>
            <Icon
              $fontSize={`${theme.font.size['4xl']}px`}
              color={theme.color.type.secondary}
              iconString="folderOpen"
            />
            <Text type="secondary">{emptyMessage}</Text>
          </Flex>
        ) : (
          <Image.PreviewGroup preview={enablePreview}>
            <Flex wrap="wrap" gap={16}>
              {allItems.map(file =>
                file.isImage && file.imageSrc ? (
                  <Image
                    key={file.id}
                    alt={file.name}
                    height={300}
                    src={file.imageSrc}
                    style={{ visibility: isLoading ? 'hidden' : 'visible' }}
                    width={300}
                    preview={enablePreview ? { mask: 'Preview', src: file.imageSrc } : false}
                  />
                ) : (
                  <FileCard
                    key={file.id}
                    fileId={file.id}
                    fileName={file.name}
                    onOpen={file.driveFileId ? handleOpenDocFile(file.driveFileId, file.name) : undefined}
                  />
                ),
              )}
            </Flex>
          </Image.PreviewGroup>
        )}
      </Flex>

      <DocPreviewModal
        isOpen={isOpenDocPreviewModal}
        url={url}
        fileName={fileName}
        isLoading={isLoadingDocPreviewModal}
        onClose={closeDocPreviewModal}
      />
    </>
  );
};
